import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import { format } from 'date-fns';
import Swal from 'sweetalert2';
import './AuditPage.css';
import { getOrgPermissions } from '../../utils/permissions';
import { isSoc2, isSoc1, catPrefix, TSC_LABEL_KEYS, formatAuditType } from '../../utils/tsc';
import i18n from '../../i18n';

function fmt(dateStr) {
  if (!dateStr) return '—';
  try { return format(new Date(dateStr + (dateStr.includes('T') ? 'Z' : '')), 'MMM dd, yyyy'); }
  catch { return '—'; }
}

function passRateColor(rate) {
  if (rate >= 80) return '#16a34a';
  if (rate >= 50) return '#ca8a04';
  return '#dc2626';
}

function StatusBadge({ status }) {
  if (!status) return <span className="au-status-badge au-status-norun">{i18n.t('AuditPage.statusNotRun')}</span>;
  if (status === 'PASS') return (
    <span className="au-status-badge au-status-pass"><i className="fas fa-check" /> {i18n.t('AuditPage.statusPass')}</span>
  );
  if (status === 'FAIL') return (
    <span className="au-status-badge au-status-fail"><i className="fas fa-times" /> {i18n.t('AuditPage.statusFail')}</span>
  );
  return <span className="au-status-badge">{status}</span>;
}

function AuditPage() {
  const { t } = useTranslation();
  const [posture,       setPosture]       = useState([]);
  const [auditLogs,     setAuditLogs]     = useState([]);
  const [failedAlerts,  setFailedAlerts]  = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);

  const [selectedAudit, setSelectedAudit] = useState(null);
  const [detailTests,   setDetailTests]   = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [showCreate,        setShowCreate]        = useState(false);
  const [frameworks,        setFrameworks]        = useState([]);
  const [users,             setUsers]             = useState([]);
  const [frameworkId,       setFrameworkId]       = useState('');
  const [auditorId,         setAuditorId]         = useState('');
  const [externalAuditor,   setExternalAuditor]   = useState('');
  const [auditorMode,       setAuditorMode]       = useState('internal'); // 'internal' | 'external'
  const [saving,            setSaving]            = useState(false);
  const [clientOrgs,        setClientOrgs]        = useState([]);
  const [selectedOrgId,     setSelectedOrgId]     = useState('');
  const [fwLoading,         setFwLoading]         = useState(false);

  // SOC 2 type + trust-criteria selection (create modal)
  const [socReportType, setSocReportType] = useState('TYPE_1');
  const [asOfDate,     setAsOfDate]     = useState('');
  const [periodStart,  setPeriodStart]  = useState('');
  const [periodEnd,    setPeriodEnd]    = useState('');
  const [tscPillars,   setTscPillars]   = useState([]);   // optional criteria from the catalog
  const [tscChecked,   setTscChecked]   = useState({});   // shorthand -> bool
  const [scopePreview, setScopePreview] = useState(null);
  const [assetReqs,    setAssetReqs]    = useState(null); // detail-view readiness checklist
  const [editScopeOpen, setEditScopeOpen] = useState(false);
  const [scopeAudit,    setScopeAudit]    = useState(null);  // audit whose scope is being edited
  const [editChecked,   setEditChecked]   = useState({});
  const [scopeSaving,   setScopeSaving]   = useState(false);
  const isCpa = ['CPA_ADMIN', 'CPA_AUDITOR'].includes(localStorage.getItem('orgRole') || '');
  const { canCreateAudit } = getOrgPermissions();

  // Detect client org context injected by CPA Dashboard via URL hash params
  const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const clientOrgId   = hashParams.get('clientOrgId')   || null;
  const clientOrgName = hashParams.get('clientOrgName') ? decodeURIComponent(hashParams.get('clientOrgName')) : null;

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage,     setPerPage]     = useState(10);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (isCpa && clientOrgId) {
      // CPA viewing a specific client org — use scoped context endpoints
      const [fwRes, logsRes] = await Promise.allSettled([
        axiosInstance.get(`/partner/context/clients/${clientOrgId}/frameworks`),
        axiosInstance.get(`/partner/context/clients/${clientOrgId}/audits`),
      ]);
      if (fwRes.status === 'fulfilled') {
        const enrolled = (fwRes.value.data || []).filter(f => f.enrolled);
        setPosture(enrolled.map(f => ({
          frameworkName: f.displayName,
          passRate:     f.compliantPct   ?? 0,
          totalTests:   f.totalControls  ?? 0,
          passedTests:  f.compliantCount ?? 0,
          failedTests:  f.failingCount   ?? 0,
        })));
      }
      if (logsRes.status === 'fulfilled')
        setAuditLogs(Array.isArray(logsRes.value.data) ? logsRes.value.data : []);
      else
        setError(logsRes.reason?.response?.data?.message || t('AuditPage.errorLoadLogs'));
    } else {
      const [postureRes, logsRes, failedRes] = await Promise.allSettled([
        axiosInstance.get('/audit/compliance-summary'),
        axiosInstance.get('/audits'),
        axiosInstance.get('/audit/latest-failed-tests-alert'),
      ]);
      if (postureRes.status === 'fulfilled')
        setPosture(Array.isArray(postureRes.value.data) ? postureRes.value.data : []);
      if (logsRes.status === 'fulfilled')
        setAuditLogs(Array.isArray(logsRes.value.data) ? logsRes.value.data : []);
      else
        setError(logsRes.reason?.response?.data?.message || t('AuditPage.errorLoadLogs'));
      if (failedRes.status === 'fulfilled')
        setFailedAlerts(Array.isArray(failedRes.value.data) ? failedRes.value.data : []);
    }
    setLoading(false);
  }, [isCpa, clientOrgId, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const selectedFw = frameworks.find(f => String(f.id) === String(frameworkId));
  const soc2Selected = isSoc2(selectedFw);
  const soc1Selected = isSoc1(selectedFw);
  const socSelected = soc2Selected || soc1Selected;
  const soc1Fw = frameworks.find(isSoc1);
  const soc2Fw = frameworks.find(isSoc2);
  const tscSelectionCsv = ['CC', ...tscPillars.filter(pl => tscChecked[pl.shorthand]).map(pl => pl.shorthand)].join(',');

  // Load the selectable criteria (catalog categories flagged optional) when a
  // SOC 2 framework is picked, and refresh the scope preview on every toggle.
  useEffect(() => {
    if (!soc2Selected || !frameworkId) { setTscPillars([]); setScopePreview(null); return; }
    axiosInstance.get(`/framework/${frameworkId}/requirement-categories`)
      .then(r => {
        const optional = (r.data || []).filter(c => c.optional);
        setTscPillars(optional.map(c => ({ shorthand: c.shorthand, name: c.name })));
        setTscChecked(prev => {
          const next = {};
          optional.forEach(c => { next[c.shorthand] = prev[c.shorthand] || false; });
          return next;
        });
      })
      .catch(() => setTscPillars([]));
  }, [soc2Selected, frameworkId]);

  useEffect(() => {
    if (!soc2Selected || !frameworkId) return;
    axiosInstance.get(`/framework/${frameworkId}/scope-preview`, { params: { tsc: tscSelectionCsv } })
      .then(r => setScopePreview(r.data))
      .catch(() => setScopePreview(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soc2Selected, frameworkId, tscSelectionCsv]);

  const openAuditDetail = async (log) => {
    setSelectedAudit(log);
    setDetailTests([]);
    setAssetReqs(null);
    if (log.id) {
      axiosInstance.get(`/audits/${log.id}/asset-requirements`)
        .then(r => setAssetReqs(r.data))
        .catch(() => setAssetReqs(null));
    }
    if (!log.auditId && !log.frameworkName) return;
    setDetailLoading(true);
    try {
      const params = { size: 500 };
      if (log.auditId) params.auditId = log.auditId;
      else params.frameWork = log.frameworkName;
      const res = await axiosInstance.get('/audit/tests', { params });
      const list = res.data?.[0]?.auditProgressList || [];
      setDetailTests(list);
    } catch {
      setDetailTests([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const openCreateForm = async () => {
    setShowCreate(true);
    setAuditorMode('internal');
    setExternalAuditor('');
    setSelectedOrgId('');
    setFrameworks([]);
    try {
      if (isCpa) {
        if (clientOrgId) {
          // Already in a specific client context — load auditors + that org's frameworks directly
          const [audRes, fwRes] = await Promise.all([
            axiosInstance.get('/partner/cpa/org-auditors'),
            axiosInstance.get(`/partner/cpa/orgs/${clientOrgId}/frameworks`),
          ]);
          setUsers(audRes.data?.map(u => ({
            id: u.id, firstName: u.first_name, lastName: u.last_name,
            userName: u.user_name, organizationName: u.organization_name, orgRole: u.org_role,
          })) || []);
          setFrameworks(fwRes.data?.map(f => ({ id: f.id, displayName: f.display_name, name: f.name })) || []);
          setSelectedOrgId(clientOrgId);
          return;
        }
        // For CPA: load client orgs + auditors; frameworks load after org selection
        const [audRes, orgsRes] = await Promise.all([
          axiosInstance.get('/partner/cpa/org-auditors'),
          axiosInstance.get('/partner/reseller/organizations'),
        ]);
        setUsers(audRes.data?.map(u => ({
          id: u.id,
          firstName: u.first_name,
          lastName: u.last_name,
          userName: u.user_name,
          organizationName: u.organization_name,
          orgRole: u.org_role,
        })) || []);
        setClientOrgs(orgsRes.data || []);
      } else {
        const [usersRes, fwRes] = await Promise.all([
          axiosInstance.get('/users'),
          axiosInstance.get('/frameworks'),
        ]);
        setUsers(usersRes.data?.map(u => ({
          ...u,
          organizationName: u.organization?.organizationName || 'N/A',
        })) || []);
        setFrameworks(fwRes.data || []);
      }
    } catch {
      Swal.fire({ title: t('AuditPage.swalErrorTitle'), text: t('AuditPage.errorLoadFormData'), icon: 'error' });
      setShowCreate(false);
    }
  };

  const handleOrgChange = async (orgId) => {
    setSelectedOrgId(orgId);
    setFrameworkId('');
    setFrameworks([]);
    if (!orgId) return;
    setFwLoading(true);
    try {
      const res = await axiosInstance.get(`/partner/cpa/orgs/${orgId}/frameworks`);
      setFrameworks(res.data?.map(f => ({ id: f.id, displayName: f.display_name, name: f.name })) || []);
    } catch {
      setFrameworks([]);
    } finally {
      setFwLoading(false);
    }
  };

  const cancelCreate = () => {
    setShowCreate(false);
    setFrameworkId('');
    setAuditorId('');
    setExternalAuditor('');
    setAuditorMode('internal');
    setSelectedOrgId('');
    setClientOrgs([]);
    setFrameworks([]);
    setSocReportType('TYPE_1');
    setAsOfDate('');
    setPeriodStart('');
    setPeriodEnd('');
    setTscChecked({});
    setScopePreview(null);
  };

  // Seeding a new/rescoped audit kicks off its in-scope automated tests. The runner allows one
  // run per org at a time, so this can come back 409 when another run already holds the slot —
  // the audit is created either way, so say so quietly rather than swallowing it silently.
  const kickOffScopedRun = (auditDbId) =>
    axiosInstance.post(`/audits/${auditDbId}/run`).catch((err) => {
      if (err?.response?.status === 409) {
        Swal.fire({ toast: true, position: 'top-end', icon: 'info',
          title: t('AuditPage.toastRunQueuedLater'), showConfirmButton: false, timer: 4000 });
      }
    });

  const handleSaveAudit = async () => {
    if (isCpa && !selectedOrgId) {
      Swal.fire({ title: t('AuditPage.swalMissingFieldTitle'), text: t('AuditPage.validationSelectClientOrg'), icon: 'warning' });
      return;
    }
    if (!frameworkId) {
      Swal.fire({ title: t('AuditPage.swalMissingFieldTitle'), text: t('AuditPage.validationSelectFramework'), icon: 'warning' });
      return;
    }
    const isExternal = auditorMode === 'external';
    if (isExternal && !externalAuditor.trim()) {
      Swal.fire({ title: t('AuditPage.swalMissingFieldTitle'), text: t('AuditPage.validationEnterExternalAuditor'), icon: 'warning' });
      return;
    }
    if (!isExternal && !auditorId) {
      Swal.fire({ title: t('AuditPage.swalMissingFieldTitle'), text: t('AuditPage.validationSelectAuditor'), icon: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const params = { frameworkId };
      if (isExternal) params.auditorName = externalAuditor.trim();
      else params.auditorId = auditorId;
      if (isCpa && selectedOrgId) params.orgId = selectedOrgId;
      let body = null;
      if (socSelected) {
        body = { auditType: `${soc1Selected ? 'SOC1' : 'SOC2'}_${socReportType}` };
        if (soc2Selected) body.tscSelection = tscSelectionCsv;
        if (socReportType === 'TYPE_1' && asOfDate) body.asOfDate = asOfDate;
        if (socReportType === 'TYPE_2' && periodStart) body.periodStartDate = periodStart;
        if (socReportType === 'TYPE_2' && periodEnd) body.periodEndDate = periodEnd;
      }
      const res = await axiosInstance.post('/audits/create', body, { params });
      if (socSelected && res.data?.id) {
        // Kick off the scoped run for the new audit's in-scope automated tests
        kickOffScopedRun(res.data.id);
      }
      cancelCreate();
      fetchData();
    } catch (err) {
      Swal.fire({ title: t('AuditPage.swalErrorTitle'), text: err.response?.data?.message || t('AuditPage.errorCreateAudit'), icon: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const SOC2_PILLARS = [
    { code: 'A1',  labelKey: TSC_LABEL_KEYS.A1 },
    { code: 'C1',  labelKey: TSC_LABEL_KEYS.C1 },
    { code: 'PI1', labelKey: TSC_LABEL_KEYS.PI },
    { code: 'P',   labelKey: TSC_LABEL_KEYS.P },
  ];

  const openEditScope = (audit) => {
    const target = audit || selectedAudit;
    if (!target) return;
    const current = target.tscSelection
      ? target.tscSelection.split(',').map(c => c.trim().toUpperCase())
      : SOC2_PILLARS.map(pl => pl.code); // null selection = full scope
    const next = {};
    SOC2_PILLARS.forEach(pl => { next[pl.code] = current.includes(pl.code); });
    setEditChecked(next);
    setScopeAudit(target);
    setEditScopeOpen(true);
  };

  const saveScope = async () => {
    const target = scopeAudit || selectedAudit;
    if (!target?.id) return;
    setScopeSaving(true);
    try {
      const csv = ['CC', ...SOC2_PILLARS.filter(pl => editChecked[pl.code]).map(pl => pl.code)].join(',');
      const res = await axiosInstance.put(`/audits/${target.id}/scope`, { tscSelection: csv });
      const newTsc = res.data?.tscSelection || csv;
      // keep the open detail view in sync when it's the same audit
      setSelectedAudit(prev => (prev && prev.id === target.id ? { ...prev, tscSelection: newTsc } : prev));
      setEditScopeOpen(false);
      // run the newly in-scope automated tests so the audit updates itself
      if ((res.data?.rowsAdded || 0) > 0 || (res.data?.rowsBroughtIntoScope || 0) > 0) {
        kickOffScopedRun(target.id);
      }
      if (selectedAudit && selectedAudit.id === target.id) {
        axiosInstance.get(`/audits/${target.id}/asset-requirements`)
          .then(r => setAssetReqs(r.data)).catch(() => {});
      }
      fetchData();
    } catch (err) {
      Swal.fire({ title: t('AuditPage.swalErrorTitle'), text: err.response?.data?.message || t('AuditPage.errorUpdateScope'), icon: 'error' });
    } finally {
      setScopeSaving(false);
    }
  };

  // Shared edit-scope modal — rendered from both the detail view and the audit list.
  const editScopeModal = editScopeOpen && (
    <div className="au-overlay no-print" onClick={() => setEditScopeOpen(false)}>
      <div className="au-modal" onClick={e => e.stopPropagation()}>
        <div className="au-modal-header">
          <span><i className="fas fa-sliders" /> {t('AuditPage.editScope')}</span>
          <button className="au-modal-close" onClick={() => setEditScopeOpen(false)}>
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="au-modal-body">
          <div className="au-field">
            <label>{t('AuditPage.labelTrustCriteria')}</label>
            <div className="au-tsc-grid">
              <label className="au-tsc-item au-tsc-locked">
                <input type="checkbox" checked disabled />
                <span><strong>{t(TSC_LABEL_KEYS.CC)}</strong><em>{t('AuditPage.tscMandatoryHint')}</em></span>
              </label>
              {SOC2_PILLARS.map(pl => (
                <label key={pl.code} className="au-tsc-item">
                  <input type="checkbox" checked={!!editChecked[pl.code]}
                    onChange={e => setEditChecked(prev => ({ ...prev, [pl.code]: e.target.checked }))} />
                  <span><strong>{t(pl.labelKey)}</strong><em>{pl.code}</em></span>
                </label>
              ))}
            </div>
            <p className="au-field-hint">{t('AuditPage.editScopeHint')}</p>
          </div>
        </div>
        <div className="au-modal-footer">
          <button className="au-btn-secondary" onClick={() => setEditScopeOpen(false)}>{t('AuditPage.cancel')}</button>
          <button className="au-btn-primary" onClick={saveScope} disabled={scopeSaving}>
            {scopeSaving ? <><i className="fas fa-spinner fa-spin" /> {t('AuditPage.saving')}</> : t('AuditPage.saveScope')}
          </button>
        </div>
      </div>
    </div>
  );

  /* ── Detail / Report view ─────────────────────────────────────────── */
  if (selectedAudit) {
    const fw         = posture.find(p => p.frameworkName === selectedAudit.frameworkName);
    const total      = fw?.totalTests  ?? 0;
    const passed     = fw?.passedTests ?? 0;
    const failed     = fw?.failedTests ?? 0;
    const norun      = Math.max(0, total - passed - failed);
    const passRate   = fw?.passRate    ?? 0;

    const failTests  = detailTests.filter(t => t.lastAuditStatus === 'FAIL');
    const passTests  = detailTests.filter(t => t.lastAuditStatus === 'PASS');
    const pendTests  = detailTests.filter(t => !t.lastAuditStatus);

    return (
      <div className="au-page au-print-scope">
        {/* ── Top bar (hidden on print) ── */}
        <div className="au-detail-topbar no-print">
          <button className="au-back-btn"
            onClick={() => { setSelectedAudit(null); setDetailTests([]); }}>
            <i className="fas fa-arrow-left" /> {t('AuditPage.backToAudits')}
          </button>
          {selectedAudit.id && (selectedAudit.auditType || '').startsWith('SOC2') && canCreateAudit && (
            <button className="au-print-btn" onClick={() => openEditScope(selectedAudit)}>
              <i className="fas fa-sliders" /> {t('AuditPage.editScope')}
            </button>
          )}
          <button className="au-print-btn" onClick={() => window.print()}>
            <i className="fas fa-print" /> {t('AuditPage.printReport')}
          </button>
        </div>

        {editScopeModal}

        {/* ── Report header (shown on print) ── */}
        <div className="au-report-header">
          <div className="au-report-title">{t('AuditPage.auditReport')}</div>
          <div className="au-report-meta">
            {selectedAudit.auditId && (
              <span className="au-report-id">{selectedAudit.auditId}</span>
            )}
            {selectedAudit.frameworkName && (
              <span className="au-fw-chip">{selectedAudit.frameworkName}</span>
            )}
          </div>
          {(selectedAudit.auditType || '').startsWith('SOC') && (
            <p className="au-scope-statement">
              {t('AuditPage.scopeStatement', {
                type: formatAuditType(selectedAudit.auditType),
                scope: selectedAudit.tscSelection
                  ? selectedAudit.tscSelection.split(',')
                      .filter(c => !['CC', 'SD'].includes(c.trim().toUpperCase()))
                      .map(c => {
                        const key = TSC_LABEL_KEYS[catPrefix(c.trim())];
                        return key ? t(key) : c.trim();
                      })
                      .reduce((acc, l) => acc.includes(l) ? acc : [...acc, l],
                        [t(TSC_LABEL_KEYS.CC)])
                      .join(', ')
                  : t('AuditPage.tscFullScope'),
              })}
            </p>
          )}
        </div>

        {/* ── Audit metadata ── */}
        <div className="au-card">
          <div className="au-card-header">{t('AuditPage.auditDetails')}</div>
          <div className="au-detail-grid">
            {[
              [t('AuditPage.fieldAuditId'),      <span className="au-mono">{selectedAudit.auditId || '—'}</span>],
              [t('AuditPage.fieldFramework'),    selectedAudit.frameworkName || '—'],
              [t('AuditPage.fieldAuditor'),      selectedAudit.performedBy || '—'],
              [t('AuditPage.fieldCreatedBy'),    selectedAudit.auditCreatedBy || '—'],
              [t('AuditPage.fieldOrganization'), selectedAudit.organization || '—'],
              [t('AuditPage.fieldDate'),         fmt(selectedAudit.startDate)],
              [t('AuditPage.fieldAuditType'),
                selectedAudit.auditType
                  ? <span className="au-fw-chip">{formatAuditType(selectedAudit.auditType)}</span>
                  : '—'],
              [t('AuditPage.fieldTscScope'),
                selectedAudit.tscSelection
                  ? <span className="au-mono">{selectedAudit.tscSelection}</span>
                  : t('AuditPage.tscFullScope')],
            ].map(([label, val]) => (
              <div key={label} className="au-detail-block">
                <p className="au-detail-label">{label}</p>
                <p className="au-detail-val">{val}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Audit readiness checklist (asset demands from criteria scope) ── */}
        {assetReqs && (assetReqs.requirements || []).length > 0 && (
          <div className="au-card">
            <div className="au-card-header">
              {t('AuditPage.readinessChecklist')}
              {assetReqs.missingCount > 0 && (
                <span className="au-checklist-missing">
                  {t('AuditPage.checklistMissing', { count: assetReqs.missingCount })}
                </span>
              )}
            </div>
            <div className="au-checklist">
              {assetReqs.requirements.map(r => (
                <div key={r.vendor} className={`au-checklist-row ${r.status === 'MISSING' ? 'is-missing' : 'is-ok'}`}>
                  <i className={`fas ${r.status === 'MISSING' ? 'fa-circle-exclamation' : 'fa-circle-check'}`} />
                  <span className="au-checklist-vendor">
                    {r.vendor === 'GENERIC' ? t('AuditPage.checklistGeneric') : r.vendor}
                  </span>
                  <span className="au-checklist-meta">
                    {t('AuditPage.checklistTests', { count: r.testCount })}
                    {r.assetClasses?.length > 0 && ` · ${r.assetClasses.join(', ')}`}
                  </span>
                  {r.status === 'MISSING' && (
                    <span className="au-checklist-action">{t('AuditPage.checklistConnect', { vendor: r.vendor })}</span>
                  )}
                </div>
              ))}
              {assetReqs.manualEvidenceControls > 0 && (
                <div className="au-checklist-row is-manual">
                  <i className="fas fa-file-lines" />
                  <span className="au-checklist-vendor">{t('AuditPage.checklistManualEvidence')}</span>
                  <span className="au-checklist-meta">
                    {t('AuditPage.checklistManualCount', { count: assetReqs.manualEvidenceControls })}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Framework posture ── */}
        {fw && (
          <div className="au-card">
            <div className="au-card-header">
              {t('AuditPage.frameworkCompliancePosture', { framework: selectedAudit.frameworkName })}
            </div>
            <div className="au-posture-stat-bar">
              {[
                [t('AuditPage.statPassRate'),   `${passRate.toFixed(1)}%`, passRateColor(passRate)],
                [t('AuditPage.statTotalTests'), total,   '#1e293b'],
                [t('AuditPage.statPassed'),     passed,  '#16a34a'],
                [t('AuditPage.statFailed'),     failed,  '#dc2626'],
                [t('AuditPage.statNotRun'),     norun,   '#94a3b8'],
              ].map(([label, val, color]) => (
                <div key={label} className="au-posture-stat">
                  <span className="au-posture-stat-val" style={{ color }}>{val}</span>
                  <span className="au-posture-stat-label">{label}</span>
                </div>
              ))}
            </div>
            {total > 0 && (
              <div className="au-progress-wrap">
                <div className="au-progress-track">
                  {passed > 0 && (
                    <div className="au-progress-seg au-prog-pass"
                      style={{ width: `${(passed / total) * 100}%` }} />
                  )}
                  {failed > 0 && (
                    <div className="au-progress-seg au-prog-fail"
                      style={{ width: `${(failed / total) * 100}%` }} />
                  )}
                  {norun > 0 && (
                    <div className="au-progress-seg au-prog-norun"
                      style={{ width: `${(norun / total) * 100}%` }} />
                  )}
                </div>
                <div className="au-progress-legend">
                  <span className="au-leg-item"><span className="au-leg-dot au-leg-pass" />{t('AuditPage.legendPassed', { count: passed })}</span>
                  <span className="au-leg-item"><span className="au-leg-dot au-leg-fail" />{t('AuditPage.legendFailed', { count: failed })}</span>
                  <span className="au-leg-item"><span className="au-leg-dot au-leg-norun" />{t('AuditPage.legendNotRun', { count: norun })}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Test results ── */}
        <div className="au-card">
          <div className="au-card-header">
            {t('AuditPage.testResults')}
            {detailTests.length > 0 && (
              <div className="au-card-header-chips">
                <span className="au-chip-pass">{t('AuditPage.chipPassed', { count: passTests.length })}</span>
                <span className="au-chip-fail">{t('AuditPage.chipFailed', { count: failTests.length })}</span>
                <span className="au-chip-norun">{t('AuditPage.chipPending', { count: pendTests.length })}</span>
              </div>
            )}
          </div>
          {detailLoading ? (
            <div className="au-loading"><div className="au-spinner" /></div>
          ) : (!selectedAudit.auditId && !selectedAudit.frameworkName) ? (
            <div className="au-empty">{t('AuditPage.emptyNoIdentifier')}</div>
          ) : detailTests.length === 0 ? (
            <div className="au-empty">{t('AuditPage.emptyNoTests')}</div>
          ) : (
            <div className="au-table-wrap">
              <table className="au-table">
                <thead>
                  <tr>
                    <th>{t('AuditPage.colTestName')}</th>
                    <th>{t('AuditPage.colCategory')}</th>
                    <th>{t('AuditPage.colControl')}</th>
                    <th>{t('AuditPage.colType')}</th>
                    <th>{t('AuditPage.colOwner')}</th>
                    <th>{t('AuditPage.colStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {detailTests.map((t, i) => (
                    <tr key={i} className={`au-row ${t.lastAuditStatus === 'FAIL' ? 'au-row-fail' : ''}`}>
                      <td className="au-test-name">{t.testName}</td>
                      <td>{t.testCategory || '—'}</td>
                      <td className="au-mono">{t.controlId || '—'}</td>
                      <td>
                        <span className={`au-type-badge ${t.type === 'AUTOMATED' ? 'au-type-auto' : 'au-type-manual'}`}>
                          {t.type || 'MANUAL'}
                        </span>
                      </td>
                      <td>{t.testExecutioner || '—'}</td>
                      <td><StatusBadge status={t.lastAuditStatus} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Print footer ── */}
        <div className="au-print-footer print-only">
          <p>{t('AuditPage.printFooter', { date: format(new Date(), 'MMMM dd, yyyy') })}</p>
        </div>
      </div>
    );
  }

  /* ── Main view ───────────────────────────────────────────────────── */
  const totalPages = Math.max(1, Math.ceil(auditLogs.length / perPage));
  const pageStart  = (currentPage - 1) * perPage;
  const pageLogs   = auditLogs.slice(pageStart, pageStart + perPage);

  return (
    <div className="au-page">
      {/* Create modal */}
      {showCreate && (
        <div className="au-overlay" onClick={cancelCreate}>
          <div className="au-modal" onClick={e => e.stopPropagation()}>
            <div className="au-modal-header">
              <span><i className="fas fa-plus" /> {t('AuditPage.newAudit')}</span>
              <button className="au-modal-close" onClick={cancelCreate}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="au-modal-body">
              {isCpa && !clientOrgId && (
                <div className="au-field">
                  <label>{t('AuditPage.labelClientOrgRequired')}</label>
                  <select className="au-input" value={selectedOrgId}
                    onChange={e => handleOrgChange(e.target.value)}>
                    <option value="">{t('AuditPage.optionSelectClientOrg')}</option>
                    {clientOrgs.map(o => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                  {clientOrgs.length === 0 && (
                    <p className="au-field-hint">{t('AuditPage.hintNoClientOrgs')}</p>
                  )}
                </div>
              )}
              {isCpa && clientOrgId && (
                <div className="au-field">
                  <label>{t('AuditPage.labelClientOrg')}</label>
                  <p className="au-field-hint" style={{ marginTop: 4 }}>
                    <i className="fas fa-building" style={{ marginRight: 6 }} />{clientOrgName}
                  </p>
                </div>
              )}

              <div className="au-field">
                <label>{t('AuditPage.labelFrameworkRequired')}</label>
                {fwLoading ? (
                  <div className="au-field-hint"><i className="fas fa-spinner fa-spin" /> {t('AuditPage.loadingFrameworks')}</div>
                ) : (
                  <select className="au-input" value={frameworkId}
                    onChange={e => setFrameworkId(e.target.value)}
                    disabled={isCpa && !selectedOrgId}>
                    <option value="">{isCpa && !selectedOrgId ? t('AuditPage.optionSelectOrgFirst') : t('AuditPage.optionSelectFramework')}</option>
                    {frameworks.map(fw => (
                      <option key={fw.id} value={fw.id}>{fw.displayName || fw.name}</option>
                    ))}
                  </select>
                )}
                {isCpa && selectedOrgId && !fwLoading && frameworks.length === 0 && (
                  <p className="au-field-hint">
                    {t('AuditPage.hintNoFrameworks')}
                  </p>
                )}
              </div>

              {socSelected && (
                <>
                  {soc1Fw && soc2Fw && (
                    <div className="au-field">
                      <label>{t('AuditPage.labelSocVariant')}</label>
                      <div className="au-auditor-toggle">
                        <button type="button"
                          className={`au-toggle-btn ${soc1Selected ? 'active' : ''}`}
                          onClick={() => setFrameworkId(String(soc1Fw.id))}>
                          {t('AuditPage.socVariant1')}
                        </button>
                        <button type="button"
                          className={`au-toggle-btn ${soc2Selected ? 'active' : ''}`}
                          onClick={() => setFrameworkId(String(soc2Fw.id))}>
                          {t('AuditPage.socVariant2')}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="au-field">
                    <label>{t('AuditPage.labelAuditType')}</label>
                    <div className="au-auditor-toggle">
                      <button type="button"
                        className={`au-toggle-btn ${socReportType === 'TYPE_1' ? 'active' : ''}`}
                        onClick={() => setSocReportType('TYPE_1')}>
                        {t('AuditPage.soc2Type1')}
                      </button>
                      <button type="button"
                        className={`au-toggle-btn ${socReportType === 'TYPE_2' ? 'active' : ''}`}
                        onClick={() => setSocReportType('TYPE_2')}>
                        {t('AuditPage.soc2Type2')}
                      </button>
                    </div>
                    {socReportType === 'TYPE_1' ? (
                      <input className="au-input" type="date" value={asOfDate}
                        onChange={e => setAsOfDate(e.target.value)}
                        aria-label={t('AuditPage.labelAsOfDate')} />
                    ) : (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input className="au-input" type="date" value={periodStart}
                          onChange={e => setPeriodStart(e.target.value)}
                          aria-label={t('AuditPage.labelPeriodStart')} />
                        <input className="au-input" type="date" value={periodEnd}
                          onChange={e => setPeriodEnd(e.target.value)}
                          aria-label={t('AuditPage.labelPeriodEnd')} />
                      </div>
                    )}
                  </div>

                  {soc2Selected && (
                  <div className="au-field">
                    <label>{t('AuditPage.labelTrustCriteria')}</label>
                    <div className="au-tsc-grid">
                      <label className="au-tsc-item au-tsc-locked">
                        <input type="checkbox" checked disabled />
                        <span>
                          <strong>{t(TSC_LABEL_KEYS.CC)}</strong>
                          <em>{t('AuditPage.tscMandatoryHint')}</em>
                        </span>
                      </label>
                      {tscPillars.map(pl => (
                        <label key={pl.shorthand} className="au-tsc-item">
                          <input type="checkbox"
                            checked={!!tscChecked[pl.shorthand]}
                            onChange={e => setTscChecked(prev => ({ ...prev, [pl.shorthand]: e.target.checked }))} />
                          <span>
                            <strong>{TSC_LABEL_KEYS[catPrefix(pl.shorthand)] ? t(TSC_LABEL_KEYS[catPrefix(pl.shorthand)]) : pl.name}</strong>
                            <em>{pl.shorthand}</em>
                          </span>
                        </label>
                      ))}
                    </div>
                    {scopePreview && (
                      <div className="au-scope-preview">
                        <span>{t('AuditPage.previewControls', { count: scopePreview.controls })}</span>
                        <span>{t('AuditPage.previewAutomated', { count: scopePreview.automatedTests })}</span>
                        <span>{t('AuditPage.previewManual', { count: scopePreview.manualEvidenceControls })}</span>
                        {(scopePreview.requiredAssetClasses || []).filter(a => !a.satisfied).length > 0 && (
                          <span className="au-scope-warn">
                            <i className="fas fa-triangle-exclamation" />{' '}
                            {t('AuditPage.previewMissingVendors', {
                              vendors: scopePreview.requiredAssetClasses.filter(a => !a.satisfied).map(a => a.vendor).join(', '),
                            })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  )}
                </>
              )}

              <div className="au-field">
                <label>{t('AuditPage.labelAuditorRequired')}</label>
                {isCpa ? (
                  <>
                    <div className="au-auditor-toggle">
                      <button
                        className={`au-toggle-btn ${auditorMode === 'internal' ? 'active' : ''}`}
                        type="button"
                        onClick={() => { setAuditorMode('internal'); setExternalAuditor(''); }}>
                        <i className="fas fa-users" /> {t('AuditPage.fromMyTeam')}
                      </button>
                      <button
                        className={`au-toggle-btn ${auditorMode === 'external' ? 'active' : ''}`}
                        type="button"
                        onClick={() => { setAuditorMode('external'); setAuditorId(''); }}>
                        <i className="fas fa-user-plus" /> {t('AuditPage.externalAuditor')}
                      </button>
                    </div>

                    {auditorMode === 'internal' ? (
                      <select className="au-input" value={auditorId}
                        onChange={e => setAuditorId(e.target.value)}>
                        <option value="">{t('AuditPage.optionSelectFromTeam')}</option>
                        {users.map(u => (
                          <option key={u.id} value={u.id}>
                            {u.firstName} {u.lastName}
                            {u.orgRole ? ` (${u.orgRole.replace('_', ' ')})` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="au-input"
                        type="text"
                        placeholder={t('AuditPage.placeholderExternalAuditor')}
                        value={externalAuditor}
                        onChange={e => setExternalAuditor(e.target.value)}
                        autoFocus
                      />
                    )}
                  </>
                ) : (
                  <select className="au-input" value={auditorId}
                    onChange={e => setAuditorId(e.target.value)}>
                    <option value="">{t('AuditPage.optionSelectAuditor')}</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.firstName} {u.lastName} — {u.organizationName}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="au-modal-footer">
              <button className="au-btn-secondary" onClick={cancelCreate}>{t('AuditPage.cancel')}</button>
              <button className="au-btn-primary" onClick={handleSaveAudit} disabled={saving}>
                {saving ? <><i className="fas fa-spinner fa-spin" /> {t('AuditPage.saving')}</> : t('AuditPage.createAudit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Page header — always visible ── */}
      <div className="au-page-header">
        <div>
          <h2 className="au-page-title">
            <i className="fas fa-clipboard-check" /> {clientOrgName ? t('AuditPage.pageTitleWithOrg', { org: clientOrgName }) : t('AuditPage.pageTitle')}
          </h2>
          <p className="au-page-subtitle">{t('AuditPage.pageSubtitle')}</p>
        </div>
        {canCreateAudit && (
          <button className="au-create-btn" onClick={openCreateForm}>
            <i className="fas fa-plus" /> {t('AuditPage.newAudit')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="au-card">
          <div className="au-loading"><div className="au-spinner" /></div>
        </div>
      ) : (
        <>
          {/* ── Compliance Posture section ── */}
          {posture.length > 0 && (
            <div className="au-section">
              <div className="au-section-header">
                <div>
                  <h3 className="au-section-title">
                    <i className="fas fa-shield-halved" /> {t('AuditPage.compliancePosture')}
                  </h3>
                  <span className="au-section-sub">
                    {t('AuditPage.compliancePostureSub')}
                  </span>
                </div>
              </div>
              <div className="au-posture-grid">
                {posture.map((fw, i) => {
                  const rate   = fw.passRate   ?? 0;
                  const tot    = fw.totalTests  ?? 0;
                  const pass   = fw.passedTests ?? 0;
                  const fail   = fw.failedTests ?? 0;
                  const norun  = Math.max(0, tot - pass - fail);
                  return (
                    <div key={i} className="au-posture-card">
                      <div className="au-posture-card-top">
                        <span className="au-posture-fw">{fw.frameworkName}</span>
                        <span className="au-posture-rate" style={{ color: passRateColor(rate) }}>
                          {rate.toFixed(1)}%
                        </span>
                      </div>
                      <div className="au-posture-track">
                        {pass > 0 && (
                          <div className="au-posture-seg au-prog-pass"
                            style={{ width: tot > 0 ? `${(pass / tot) * 100}%` : '0%' }} />
                        )}
                        {fail > 0 && (
                          <div className="au-posture-seg au-prog-fail"
                            style={{ width: tot > 0 ? `${(fail / tot) * 100}%` : '0%' }} />
                        )}
                        {norun > 0 && (
                          <div className="au-posture-seg au-prog-norun"
                            style={{ width: tot > 0 ? `${(norun / tot) * 100}%` : '100%' }} />
                        )}
                      </div>
                      <div className="au-posture-counts">
                        <span className="au-cnt-pass">{t('AuditPage.countPassed', { count: pass })}</span>
                        <span className="au-cnt-fail">{t('AuditPage.countFailed', { count: fail })}</span>
                        <span className="au-cnt-norun">{t('AuditPage.countPending', { count: norun })}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Failing alerts ── */}
          {failedAlerts.length > 0 && (
            <div className="au-alert-bar">
              <i className="fas fa-triangle-exclamation au-alert-icon" />
              <span className="au-alert-text">
                {t('AuditPage.alertTestsFailing', { count: failedAlerts.length })}
              </span>
              <div className="au-alert-pills">
                {failedAlerts.slice(0, 5).map((t, i) => (
                  <span key={i} className="au-alert-pill">{t.testName}</span>
                ))}
                {failedAlerts.length > 5 && (
                  <span className="au-alert-more">{t('AuditPage.alertMore', { count: failedAlerts.length - 5 })}</span>
                )}
              </div>
            </div>
          )}

          {/* ── Audit history ── */}
          <div className="au-section">
            <div className="au-section-header">
              <div>
                <h3 className="au-section-title">
                  <i className="fas fa-history" /> {t('AuditPage.auditHistory')}
                </h3>
                <span className="au-section-sub">
                  {t('AuditPage.auditHistorySub')}
                </span>
              </div>
            </div>

            <div className="au-card">
              {error ? (
                <div className="au-error" style={{ margin: '1rem' }}>
                  <i className="fas fa-exclamation-circle" /> {error}
                  <button className="au-retry" onClick={fetchData}>{t('AuditPage.retry')}</button>
                </div>
              ) : (
                <>
                  <div className="au-table-wrap">
                    <table className="au-table">
                      <thead>
                        <tr>
                          <th>{t('AuditPage.colAuditId')}</th>
                          <th>{t('AuditPage.colFramework')}</th>
                          <th>{t('AuditPage.colType')}</th>
                          <th>{t('AuditPage.colAuditor')}</th>
                          <th>{t('AuditPage.colCreatedBy')}</th>
                          <th>{t('AuditPage.colDate')}</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageLogs.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="au-empty">
                              <i className="fas fa-clipboard" />
                              <p>{t('AuditPage.emptyNoAudits', { button: t('AuditPage.newAudit') })}</p>
                            </td>
                          </tr>
                        ) : pageLogs.map((log, i) => (
                          <tr key={i} className="au-row au-row-click"
                            onClick={() => openAuditDetail(log)}>
                            <td className="au-mono au-audit-id">{log.auditId || '—'}</td>
                            <td>
                              {log.frameworkName
                                ? <span className="au-fw-chip">{log.frameworkName}</span>
                                : <span className="au-muted">—</span>}
                            </td>
                            <td>
                              {log.auditType
                                ? <span className="au-fw-chip">{formatAuditType(log.auditType, true)}</span>
                                : <span className="au-muted">—</span>}
                              {log.tscSelection && (
                                <div className="au-scope-codes">{log.tscSelection}</div>
                              )}
                            </td>
                            <td>{log.performedBy || '—'}</td>
                            <td>{log.auditCreatedBy || '—'}</td>
                            <td className="au-date">{fmt(log.startDate)}</td>
                            <td className="au-actions">
                              {(log.auditType || '').startsWith('SOC2') && canCreateAudit && (
                                <button className="au-view-btn au-scope-btn"
                                  onClick={e => { e.stopPropagation(); openEditScope(log); }}>
                                  <i className="fas fa-sliders" /> {t('AuditPage.editScope')}
                                </button>
                              )}
                              <button className="au-view-btn"
                                onClick={e => { e.stopPropagation(); openAuditDetail(log); }}>
                                {t('AuditPage.report')} <i className="fas fa-chevron-right" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {auditLogs.length > perPage && (
                    <div className="au-pagination">
                      <div className="au-page-info">
                        {t('AuditPage.paginationRange', { from: pageStart + 1, to: Math.min(pageStart + perPage, auditLogs.length), total: auditLogs.length })}
                        <select className="au-per-page" value={perPage}
                          onChange={e => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                          {[5, 10, 20, 50].map(n => <option key={n} value={n}>{t('AuditPage.perPage', { count: n })}</option>)}
                        </select>
                      </div>
                      <div className="au-page-controls">
                        <button className="au-page-btn"
                          onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                          <i className="fas fa-angle-double-left" />
                        </button>
                        <button className="au-page-btn"
                          onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
                          <i className="fas fa-chevron-left" />
                        </button>
                        <span className="au-page-num">{t('AuditPage.pageOf', { current: currentPage, total: totalPages })}</span>
                        <button className="au-page-btn"
                          onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages}>
                          <i className="fas fa-chevron-right" />
                        </button>
                        <button className="au-page-btn"
                          onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages}>
                          <i className="fas fa-angle-double-right" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          {editScopeModal}
        </>
      )}
    </div>
  );
}

export default AuditPage;
